import { Table, TableContainer, Tbody, Td, Text, Th, Thead, Tr, useDisclosure } from '@chakra-ui/react'
import { useEffect, useMemo, useState } from 'react'

import { ActionButton, ActionFab } from '../../../components/ActionButton'
import { ConfirmDialog } from '../../../components/ConfirmDialog'
import { EmptyCell } from '../../../components/EmptyCell'
import { FilterBar, FilterDateRange, FilterSearch, FilterSelect } from '../../../components/FilterBar'
import { FormModal } from '../../../components/FormModal'
import { Icon } from '../../../components/Icon'
import { TableSkeletonRows } from '../../../components/Loading'
import { PageTitle } from '../../../components/PageTitle'
import { TableEmptyRow, TableErrorRow } from '../../../components/TableEmptyRow'
import { formatDateTime, useHasRole, useAppToast } from '../../../helpers'
import IFilter, { IRefund } from '../../../services/Refunds/interfaces'
import RefundsService from '../../../services/Refunds/RefundsService'
import { ISector, SectorService } from '../../../services/Sector/SectorService'
import RefundsEditValueModal from './RefundsEditValueModal'
import RefundsViewPage from './RefundsViewPage'

const INITIAL_FILTER: IFilter = {
  name: '',
  type: '',
  sectorId: 0,
  date: {
    start: '',
    end: ''
  }
}

const TYPE_OPTIONS = [
  { value: 'Comum', label: 'Comum' },
  { value: 'KMs Rodados', label: 'KMs Rodados' }
]

export default function RefundsPage() {
  const modalState = useDisclosure()
  const editValueModalState = useDisclosure()
  const [refundId, setRefundId] = useState<number>()
  const hasRole = useHasRole()
  const toast = useAppToast()
  const refundsService = useMemo(() => new RefundsService(), [])
  const [refunds, setRefunds] = useState<IRefund[]>([])
  const [loading, setLoading] = useState(true)
  const [loadError, setLoadError] = useState(false)
  const sectorService = useMemo(() => new SectorService(), [])
  const [sectors, setSectors] = useState<ISector[]>([])
  const permissions = {
    addRefunds: hasRole('add_refunds'),
    deleteRefunds: hasRole('delete_refunds')
  }
  const [filter, setFilter] = useState<IFilter>(INITIAL_FILTER)
  const isDirty =
    filter.name !== '' ||
    filter.type !== '' ||
    filter.sectorId !== 0 ||
    filter.date.start !== '' ||
    filter.date.end !== ''
  const [deletingId, setDeletingId] = useState(0)
  const [editValueRefund, setEditValueRefund] = useState('')

  const fetchRefunds = async () => {
    setLoading(true)
    setLoadError(false)
    try {
      const response = await refundsService.list(filter)
      setRefunds(response.data)
    } catch (error) {
      setLoadError(true)
      toast.error('Erro ao carregar os reembolsos', error)
    } finally {
      setLoading(false)
    }
  }

  const fetchSectors = async () => {
    try {
      const response = await sectorService.listForSelect()
      setSectors(response)
    } catch {
      toast.error('Erro ao buscar a lista de Setores!')
    }
  }

  const handleDelete = async () => {
    try {
      await refundsService.delete(deletingId)
      fetchRefunds()

      toast.success('Cadastro de reembolso deletado com sucesso!')
    } catch {
      toast.error('Erro ao deletar cadastro de Reembolso!')
    } finally {
      setDeletingId(0)
    }
  }

  useEffect(() => {
    fetchSectors()
  }, [])

  // A busca por nome já chega debounced do `FilterSearch`; select e datas aplicam na hora.
  useEffect(() => {
    fetchRefunds()
  }, [filter])

  return (
    <>
      <PageTitle title="Reembolsos" description="Solicitações de reembolso e seu andamento" />

      {permissions.addRefunds && (
        <ActionFab to="/refunds/new" label="Criar nova solicitação de reembolso" openInNewTab />
      )}

      <FilterBar onClear={() => setFilter(INITIAL_FILTER)} isDirty={isDirty}>
        <FilterSearch
          label="Nome"
          value={filter.name}
          onChange={(name) => setFilter({ ...filter, name })}
          placeholder="Buscar por nome"
        />
        <FilterSelect
          label="Tipo"
          value={filter.type}
          onChange={(type) => setFilter({ ...filter, type })}
          options={TYPE_OPTIONS}
        />
        <FilterSelect
          label="Setor"
          value={filter.sectorId}
          onChange={(sectorId) => setFilter({ ...filter, sectorId: Number(sectorId) })}
          options={sectors.map((sector) => ({ value: sector.id, label: sector.name }))}
          // A listagem trata `0` como "sem filtro de setor", não `''`.
          emptyValue={0}
        />
        <FilterDateRange label="Data" value={filter.date} onChange={(date) => setFilter({ ...filter, date })} />
      </FilterBar>

      <TableContainer>
        <Table>
          <Thead>
            <Tr>
              <Th>#</Th>
              <Th>Nome</Th>
              <Th>Setor</Th>
              <Th>Tipo</Th>
              <Th>Valor</Th>
              <Th>Criado em</Th>
              <Th>Ações</Th>
            </Tr>
          </Thead>
          <Tbody>
            {loading ? (
              <TableSkeletonRows cols={7} />
            ) : loadError ? (
              <TableErrorRow colSpan={7} entity="reembolsos" onRetry={fetchRefunds} />
            ) : refunds.length === 0 ? (
              <TableEmptyRow colSpan={7} entity="reembolso" />
            ) : (
              refunds.map((refund) => (
                <Tr key={refund.id}>
                  <Td>{refund.id}</Td>
                  <Td>{refund.name}</Td>
                  <Td>{sectors.find((sector) => sector.id === refund.sector_id)?.name}</Td>
                  <Td>
                    <EmptyCell value={refund?.type} />
                  </Td>
                  <Td>
                    {refund?.value || (
                      <Text as="span" color="gray.500">
                        Não informado
                      </Text>
                    )}
                  </Td>
                  <Td>{formatDateTime(refund.created_at)}</Td>
                  <Td>
                    {refund.is_done && (
                      <>
                        <ActionButton
                          action="edit"
                          label="Editar valor"
                          colorScheme="green"
                          icon={<Icon name="lni lni-dollar" />}
                          onClick={() => {
                            editValueModalState.onOpen()
                            setEditValueRefund(refund?.value || '')
                            setRefundId(refund.id)
                          }}
                        />
                        <ActionButton
                          action="view"
                          onClick={() => {
                            modalState.onOpen()
                            setRefundId(refund.id)
                          }}
                        />
                      </>
                    )}
                    {!refund.is_done && permissions.addRefunds && (
                      <ActionButton action="edit" to={`/refunds/${refund.id}`} />
                    )}
                    {permissions.deleteRefunds && (
                      <ActionButton
                        action="delete"
                        onClick={() => {
                          setDeletingId(refund.id)
                        }}
                      />
                    )}
                  </Td>
                </Tr>
              ))
            )}
          </Tbody>
        </Table>
      </TableContainer>

      {/* View Modal */}
      <FormModal isOpen={modalState.isOpen} onClose={modalState.onClose} size="wide" title="Solicitação de Reembolso">
        {refundId !== undefined && <RefundsViewPage refundId={refundId} sectors={sectors} />}
      </FormModal>

      {/* Edit Value Modal */}
      <FormModal isOpen={editValueModalState.isOpen} onClose={editValueModalState.onClose} size="wide">
        {refundId !== undefined && (
          <RefundsEditValueModal
            fetchRefunds={fetchRefunds}
            onClose={editValueModalState.onClose}
            refundId={refundId}
            refundValue={editValueRefund}
          />
        )}
      </FormModal>

      <ConfirmDialog
        isOpen={deletingId > 0}
        onClose={() => setDeletingId(0)}
        onConfirm={handleDelete}
        action="delete"
        entity="reembolso"
      />
    </>
  )
}
